import React from 'react';
import { Line } from 'react-chartjs-2';
import 'chart.js/auto';
import 'chartjs-adapter-date-fns';
import { formatLondon } from '../../utils/dateFormatting';
import { baseOptions, dayRange, timeAxis, valueAxis } from '../../utils/chartConfig';

const ForecastChart = ({ forecastData, heatingStatus, loading, selectedDate, plannedWindows }) => {
    const { dayStart, dayEnd } = dayRange(selectedDate);

    // The scheduler's intended actions: heating windows [preheat_start, target_time]
    // from the solved comfort objectives. Build a stepped band (0/1 on y1) plus a
    // list of on/off transition markers the inline plugin draws on the timeline.
    const windows = (plannedWindows || [])
        .map(w => ({ start: new Date(w.start).getTime(), end: new Date(w.end).getTime(), temp: w.temp }))
        .filter(w => !isNaN(w.start) && !isNaN(w.end) && w.end > w.start)
        .sort((a, b) => a.start - b.start);

    // Clip each window to the visible day before emitting band points. The schedule
    // includes next-day events; without clipping, a tomorrow window pushes points
    // past dayEnd and the trailing {dayEnd, 0} then jumps x backwards, leaving the
    // dataset non-monotonic in x — which makes Chart.js's area fill self-intersect
    // and smear a spurious wedge across the whole day. (The markers loop below
    // already clips the same way.)
    const plannedBand = [];
    if (dayStart != null) plannedBand.push({ x: dayStart, y: 0 });
    windows.forEach(w => {
        const s = dayStart != null ? Math.max(w.start, dayStart) : w.start;
        const e = dayEnd != null ? Math.min(w.end, dayEnd) : w.end;
        if (e <= s) return;  // window doesn't overlap the visible day
        plannedBand.push({ x: s, y: 0 }, { x: s, y: 1 }, { x: e, y: 1 }, { x: e, y: 0 });
    });
    if (dayEnd != null) plannedBand.push({ x: dayEnd, y: 0 });

    // Transition markers, clipped to the visible day.
    const markers = [];
    windows.forEach(w => {
        if (dayStart == null || (w.start >= dayStart && w.start < dayEnd)) {
            markers.push({ x: w.start, kind: 'on', temp: w.temp });
        }
        if (dayStart == null || (w.end >= dayStart && w.end < dayEnd)) {
            markers.push({ x: w.end, kind: 'off' });
        }
    });

    const plannedMarkersPlugin = {
        id: 'plannedMarkers',
        afterDatasetsDraw(chart) {
            const { ctx, chartArea, scales } = chart;
            if (!chartArea || !scales.x) return;
            const hhmm = (t) => formatLondon(t, 'time');
            ctx.save();
            markers.forEach(m => {
                const px = scales.x.getPixelForValue(m.x);
                if (px < chartArea.left || px > chartArea.right) return;
                const on = m.kind === 'on';
                ctx.strokeStyle = on ? 'rgba(99, 102, 241, 0.9)' : 'rgba(100, 116, 139, 0.7)'; // indigo / slate
                ctx.lineWidth = 1;
                ctx.setLineDash([4, 3]);
                ctx.beginPath();
                ctx.moveTo(px, chartArea.top);
                ctx.lineTo(px, chartArea.bottom);
                ctx.stroke();
                ctx.setLineDash([]);
                const label = on
                    ? `ON ${hhmm(m.x)}${m.temp != null ? ` · ${m.temp}°C` : ''}`
                    : `OFF ${hhmm(m.x)}`;
                ctx.font = '10px sans-serif';
                ctx.fillStyle = on ? 'rgba(79, 70, 229, 1)' : 'rgba(71, 85, 105, 1)';
                ctx.textBaseline = 'top';
                const tw = ctx.measureText(label).width;
                // Keep the label inside the chart area.
                const tx = Math.min(Math.max(px + 3, chartArea.left), chartArea.right - tw);
                ctx.fillText(label, tx, chartArea.top + 2 + (on ? 0 : 12));
            });
            ctx.restore();
        },
    };
    if (loading) {
        return (
            <div className="h-full flex flex-col items-center justify-center text-gray-400">
                <div className="w-6 h-6 border-2 border-gray-200 border-t-gray-400 rounded-full animate-spin mb-3" />
                <p className="text-sm">Loading forecast…</p>
            </div>
        );
    }
    if (!forecastData || forecastData.length === 0) {
        return (
            <div className="h-full flex flex-col items-center justify-center text-gray-400">
                <svg className="w-10 h-10 mb-2" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 17l6-6 4 4 8-8M21 7h-5M21 7v5" />
                </svg>
                <p className="text-sm">Select a device to view the temperature forecast</p>
            </div>
        );
    }

    // Palette: rose=internal (actual+predicted), sky=external, amber fill=heating
    const chartData = {
        datasets: [
            {
                label: 'Actual Internal Temp',
                data: forecastData.map(item => ({ x: item.time, y: item.actual_mean_temperature })),
                fill: false,
                pointRadius: 0,
                borderColor: 'rgba(244, 63, 94, 1)',  // rose-500
                borderWidth: 2,
                yAxisID: 'y',
                order: 2,
            },
            {
                label: 'Predicted Internal Temp',
                data: forecastData.map(item => ({ x: item.time, y: item.internal_temperature })),
                fill: false,
                pointRadius: 0,
                borderColor: 'rgba(244, 63, 94, 0.55)',
                borderWidth: 2,
                borderDash: [6, 3],
                yAxisID: 'y',
                order: 3,
            },
            {
                label: 'External Temperature',
                data: forecastData.map(item => ({ x: item.time, y: item.external_temperature })),
                fill: false,
                pointRadius: 0,
                borderColor: 'rgba(14, 165, 233, 1)',  // sky-500
                borderWidth: 2,
                yAxisID: 'y',
                order: 4,
            },
            {
                label: 'Heating Status',
                data: (heatingStatus || []).map(item => ({
                    x: item.time,
                    y: item.heating_status,
                })),
                fill: true,
                pointRadius: 0,
                borderWidth: 0,
                backgroundColor: 'rgba(251, 191, 36, 0.35)',  // amber-400 (actual)
                yAxisID: 'y1',
                order: 5,
            },
            {
                label: 'Planned Heating',
                data: plannedBand,
                fill: true,
                stepped: false,
                pointRadius: 0,
                borderColor: 'rgba(99, 102, 241, 0.5)',       // indigo-500 (planned)
                borderWidth: 1,
                backgroundColor: 'rgba(99, 102, 241, 0.14)',
                yAxisID: 'y1',
                order: 6,
                spanGaps: true,
            },
        ],
    };

    const chartOptions = baseOptions({
        label: (context) => {
            let label = context.dataset.label || '';
            if (context.parsed.y !== null) {
                label += context.dataset.yAxisID === 'y1'
                    ? ': ' + (context.parsed.y > 0.5 ? 'On' : 'Off')
                    : ': ' + context.parsed.y.toFixed(1) + '°C';
            }
            return label;
        },
        scales: {
            x: timeAxis(selectedDate),
            y: valueAxis('°C'),
            y1: {
                type: 'linear',
                display: false,
                position: 'right',
                grid: { drawOnChartArea: false },
                suggestedMin: 0,
                suggestedMax: 1,
            },
        },
    });

    return (
        <div style={{ height: '100%' }}>
            <Line data={chartData} options={chartOptions} plugins={[plannedMarkersPlugin]} />
        </div>
    );
};

export default ForecastChart;
